import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { GALLERY, IMAGES } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["All", "Senior", "Juniors", "Girls", "Ground & Pavilion"];

export default function Gallery() {
  const [category, setCategory] = useState("All");
  const [selected, setSelected] = useState(null);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/photos`)
      .then((r) =>
        setUploaded(
          r.data.map((p) => ({
            src: `${API}/files/${p.storage_path}`,
            caption: p.caption,
            category: CATEGORIES.includes(p.category) ? p.category : "Ground & Pavilion",
          }))
        )
      )
      .catch(() => {});
  }, []);

  const all = [...GALLERY, ...uploaded];
  const filtered = category === "All" ? all : all.filter((g) => g.category === category);

  useEffect(() => {
    const onKey = (e) => {
      if (selected === null) return;
      if (e.key === "Escape") setSelected(null);
      if (e.key === "ArrowRight") setSelected((s) => (s + 1) % filtered.length);
      if (e.key === "ArrowLeft") setSelected((s) => (s - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, filtered.length]);

  return (
    <main data-testid="gallery-page">
      <Seo
        title="Photo Gallery — Whitley Bay Cricket Club"
        description="Photos from West Park: the championship-winning 1st XI, junior nights, girls' cricket, All Stars and matchdays at Whitley Bay CC."
      />
      <PageHeader
        eyebrow="Life at West Park"
        title="The"
        accent="gallery."
        description="Squad photos, junior nights, matchdays and the ground we call home — straight from the club's own camera roll."
        image={IMAGES.juniorNights}
        testId="gallery-header"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => { setCategory(c); setSelected(null); }}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors ${
                category === c
                  ? "bg-[#E5AC3C] text-[#0B1520] border-[#E5AC3C]"
                  : "border-white/15 text-[#9BB0C1] hover:border-[#E5AC3C] hover:text-[#E5AC3C]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g, i) => (
            <Reveal key={g.src} delay={(i % 3) * 0.06}>
              <button
                onClick={() => setSelected(i)}
                className="group relative block w-full rounded-2xl overflow-hidden border border-white/10 hover:border-[#E5AC3C]/40 transition-colors text-left"
                data-testid={`gallery-item-${i}`}
              >
                <img src={g.src} alt={g.caption} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1520]/90 to-transparent p-4 pt-10">
                  <span className="text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">{g.category}</span>
                  <p className="text-sm font-semibold text-[#F7F5F0]">{g.caption}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {selected !== null && filtered[selected] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0B1520]/95 backdrop-blur flex items-center justify-center p-5"
            onClick={() => setSelected(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-5 right-5 text-[#F7F5F0] hover:text-[#E5AC3C] p-2 transition-colors"
              onClick={() => setSelected(null)}
              data-testid="lightbox-close-button"
              aria-label="Close"
            >
              <X size={26} />
            </button>
            <button
              className="absolute left-3 sm:left-8 text-[#F7F5F0] hover:text-[#E5AC3C] p-2 transition-colors"
              onClick={(e) => { e.stopPropagation(); setSelected((selected - 1 + filtered.length) % filtered.length); }}
              data-testid="lightbox-prev-button"
              aria-label="Previous photo"
            >
              <ChevronLeft size={34} />
            </button>
            <motion.figure
              key={filtered[selected].src}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={filtered[selected].src} alt={filtered[selected].caption} className="w-full max-h-[75vh] object-contain rounded-2xl" />
              <figcaption className="mt-4 text-center">
                <span className="text-[10px] font-mono-club uppercase tracking-[0.2em] text-[#E5AC3C] block">{filtered[selected].category}</span>
                <span className="text-sm text-[#F7F5F0]">{filtered[selected].caption}</span>
              </figcaption>
            </motion.figure>
            <button
              className="absolute right-3 sm:right-8 text-[#F7F5F0] hover:text-[#E5AC3C] p-2 transition-colors"
              onClick={(e) => { e.stopPropagation(); setSelected((selected + 1) % filtered.length); }}
              data-testid="lightbox-next-button"
              aria-label="Next photo"
            >
              <ChevronRight size={34} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
